'use client';
import React, { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Input, Icon, IConversationState, Avatar, IConversation } from '@ui/components';
import { useRouter } from 'next/navigation';
import dayjs from 'dayjs';

interface ConversationRecipientsProps {
  conversation: IConversation;
  state: IConversationState;
}

const ConversationRecipients = ({ conversation, state }: ConversationRecipientsProps) => {
  const scrollContainerId = 'recipients-scroll-container';
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');

  const {
    fans,
    loadConversationFans,
    loadMoreFans,
    hasMoreFans,
    isFansLoading,
    isFansFetching,
    handleFansSearch,
    searchTerm,
  } = state || {};

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (handleFansSearch) {
      handleFansSearch(value);
    }
  };

  useEffect(() => {
    if (conversation?.id && loadConversationFans) {
      loadConversationFans(conversation?.id);
    }
  }, [conversation?.id, loadConversationFans]);

  useEffect(() => {
    if (searchTerm !== undefined && searchTerm !== inputValue && !isFansFetching) {
      setInputValue(searchTerm);
    }
  }, [searchTerm, inputValue, isFansFetching]);

  // Get source information from conversation's miscellaneous data
  const getSourceInfo = () => {
    if (!conversation?.miscellaneous) return null;

    const source = conversation?.miscellaneous?.from;

    if (source === 'cohort') {
      return {
        type: 'Cohort',
        created_at: conversation?.miscellaneous?.created_at,
        name: conversation?.miscellaneous?.cohort_name,
        icon: 'people',
      };
    } else if (source === 'custom_leaderboard') {
      return {
        type: 'Leaderboard',
        created_at: conversation?.miscellaneous?.created_at,
        name: conversation?.miscellaneous?.custom_leaderboard_name,
        icon: 'trophy',
      };
    } else if (source === 'emails') {
      return {
        type: 'Emails',
        icon: 'message',
      };
    }

    return null;
  };

  const sourceInfo = getSourceInfo();

  return (
    <div className="flex h-full flex-col p-4">
      {/* Source information banner */}
      {sourceInfo && (
        <div className="mb-4 rounded-md bg-orange-50 p-3">
          <div className="flex items-start">
            <Icon
              type={sourceInfo?.icon as any}
              className="mr-2 h-5 w-5 text-orange-500"
              fill="#FFA500"
            />
            <div className="text-sm font-medium text-orange-700 flex flex-col justify-start">
              <div className="flex items-start ">
                Recipients from: {sourceInfo?.type}
                {sourceInfo.name && (
                  <span className="ml-1 text-orange-600 text-ellipsis max-w-[190px] overflow-hidden text-nowrap font-semibold">
                    {sourceInfo?.name}
                  </span>
                )}
              </div>
              {sourceInfo?.created_at && (
                <span className="text-orange-700 text-xs">
                  Created: {dayjs(sourceInfo?.created_at).format('DD/MM/YYYY')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center">
        <div className="relative w-full">
          <Input
            value={inputValue}
            placeholder="Search by name or email..."
            className="pr-10"
            onChange={handleInputChange}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Icon type="search" className="h-4 w-4 text-gray-400" />
          </div>
        </div>
      </div>

      {isFansLoading && !fans?.length ? (
        <div className="flex h-40 items-center justify-center">
          <Icon type="loadingSpinner" className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !fans || fans.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-gray-500">
          {searchTerm ? 'No recipients match your search' : 'No recipients found'}
        </div>
      ) : (
        <div id={scrollContainerId} className="h-[350px] overflow-y-auto pr-2">
          <InfiniteScroll
            dataLength={fans.length || 0}
            next={loadMoreFans || (() => {})}
            hasMore={fans.length > 5 ? !!hasMoreFans : false}
            scrollableTarget={scrollContainerId}
            loader={
              <div className="flex justify-center py-4">
                <Icon type="loadingSpinner" className="h-6 w-6 animate-spin text-primary" />
              </div>
            }>
            <div className="space-y-2">
              {fans.map((fan) => (
                <div
                  key={fan.id}
                  className="flex items-center rounded-md border border-gray-200 p-3 transition-colors hover:bg-gray-50 cursor-pointer"
                  onClick={() => router.push(`/fan-profile/?fanId=${fan?.fan_id}`)}>
                  <div className="mr-3 h-10 w-10 flex-shrink-0 overflow-hidden rounded-full">
                    {fan?.avatar_url ? (
                      <Avatar size="s" src={fan?.avatar_url} />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-primary text-sm font-medium text-white">
                        {(fan?.user_name || fan?.display_name || '?')[0].toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-gray-900">
                      {fan?.display_name || fan?.user_name || 'Unknown User'}
                    </div>
                    <div className="truncate text-sm text-gray-500">{fan?.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </InfiniteScroll>
        </div>
      )}

      {!isFansLoading && fans && fans.length > 0 && (
        <div className="mt-2 text-center text-sm text-gray-500">
          Showing {fans.length} {fans.length === 1 ? 'recipient' : 'recipients'}
        </div>
      )}
    </div>
  );
};

export default ConversationRecipients;
